import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  ArrowLeft,
  BookOpen,
  Gauge,
  ListTree,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Badge } from "@app/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@app/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import API from "@app/services/api";
import { useViewAsKey } from "@app/services/view-as";
import { usePreviewView } from "@app/zustand/preview-view";
import { useLabReadOnly } from "@app/utils/use-lab-read-only";
import { Trans, useLingui } from "@lingui/macro";

interface MetricsProfile {
  id: string;
  name: string;
  description: string | null;
  specialite: string | null;
  owner_id: string;
  is_system: boolean;
  derived_from_ontology_stack_id: string | null;
  derived_from_dataset_id: string | null;
  entry_count?: number;
  entries?: MetricsProfileEntry[];
}

interface MetricsProfileEntry {
  id: string;
  metrics_profile_id: string;
  field_key: string;
  evaluator_type: string;
  weight: number;
}

/** What an experiment with no profile is scored with: the speciality's ontology, read live. */
interface OntologyContract {
  specialite: string | null;
  scored_count: number;
  entries: OntologyContractEntry[];
}

interface OntologyContractEntry {
  field_key: string;
  name: string;
  group_name: string | null;
  /** `null`: the field is read but never scored. */
  evaluator_type: string | null;
  weight: number | null;
}

// Evaluator types known to the BE scorer — keep this list in sync with
// `mage_x_api/utils/precision.py` (EVALUATOR_MAP / EVALUATOR_DETAILED_MAP).
const EVALUATOR_TYPES = [
  "string",
  "number",
  "boolean",
  "enum",
  "soft_enum",
  "array_string",
  "array_enum",
  "score_series",
];

export default function LaboratoireMetriquesPage() {
  const { t } = useLingui();
  const labReadOnly = useLabReadOnly();
  const previewSpecialite = usePreviewView((state) => state.previewSpecialite);
  /** Whose platform is on screen — every read below answers for them. */
  const viewAsKey = useViewAsKey();

  const [contract, setContract] = useState<OntologyContract | null>(null);
  const [contractOpen, setContractOpen] = useState(false);
  const [profiles, setProfiles] = useState<MetricsProfile[]>([]);
  const [selected, setSelected] = useState<MetricsProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editProfile, setEditProfile] = useState<MetricsProfile | null>(null);
  const [entryEditor, setEntryEditor] = useState<{
    profileId: string;
    entry: MetricsProfileEntry | null;
  } | null>(null);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    const res = await API.get({ path: "/metrics-profile/" });
    if (res.ok && Array.isArray(res.data)) {
      setProfiles(res.data as MetricsProfile[]);
    }
    setLoading(false);
  }, []);

  const loadContract = useCallback(async () => {
    const res = await API.get({ path: "/metrics-profile/ontology-contract" });
    if (res.ok && res.data) {
      setContract(res.data as OntologyContract);
    }
  }, []);

  const loadProfileDetail = useCallback(async (profileId: string) => {
    const res = await API.get({ path: `/metrics-profile/${profileId}` });
    if (res.ok && res.data) {
      setSelected(res.data as MetricsProfile);
    }
  }, []);

  // Le backend scope la liste sur la spécialité de la session : il faut donc la relire quand
  // l'admin change de périmètre, sinon l'écran garde les profils de la spécialité précédente.
  useEffect(() => {
    void loadProfiles();
    void loadContract();
  }, [loadProfiles, loadContract, viewAsKey]);

  const handleCreate = async (data: {
    name: string;
    description: string | null;
    specialite: string | null;
  }) => {
    const res = await API.post({ path: "/metrics-profile/", body: data });
    if (!res.ok) {
      toast.error(res.error || t`Erreur lors de la création`);
      return;
    }
    await loadProfiles();
    if (res.data?.id) {
      await loadProfileDetail(res.data.id);
    }
  };

  const handleUpdateProfile = async (
    profileId: string,
    data: { name?: string; description?: string | null; specialite?: string | null },
  ) => {
    const res = await API.put({ path: `/metrics-profile/${profileId}`, body: data });
    if (!res.ok) {
      toast.error(res.error || t`Erreur lors de la mise à jour`);
      return;
    }
    await loadProfiles();
    if (selected?.id === profileId) await loadProfileDetail(profileId);
  };

  const handleDeleteProfile = async (profileId: string) => {
    if (!confirm(t`Supprimer ce profil de métriques ?`)) return;
    const res = await API.delete({ path: `/metrics-profile/${profileId}` });
    if (!res.ok) {
      toast.error(res.error || t`Erreur lors de la suppression`);
      return;
    }
    if (selected?.id === profileId) setSelected(null);
    await loadProfiles();
  };

  const handleAddEntry = async (
    profileId: string,
    data: { field_key: string; evaluator_type: string; weight: number },
  ) => {
    const res = await API.post({
      path: `/metrics-profile/${profileId}/entries`,
      body: data,
    });
    if (!res.ok) {
      toast.error(res.error || t`Erreur lors de l'ajout`);
      return;
    }
    await loadProfileDetail(profileId);
  };

  const handleUpdateEntry = async (
    profileId: string,
    entryId: string,
    data: { evaluator_type?: string; weight?: number },
  ) => {
    const res = await API.put({
      path: `/metrics-profile/${profileId}/entries/${entryId}`,
      body: data,
    });
    if (!res.ok) {
      toast.error(res.error || t`Erreur lors de la mise à jour`);
      return;
    }
    await loadProfileDetail(profileId);
  };

  const handleDeleteEntry = async (profileId: string, entryId: string) => {
    if (!confirm(t`Supprimer cette entrée ?`)) return;
    const res = await API.delete({
      path: `/metrics-profile/${profileId}/entries/${entryId}`,
    });
    if (!res.ok) {
      toast.error(res.error || t`Erreur lors de la suppression`);
      return;
    }
    await loadProfileDetail(profileId);
  };

  const sortedEntries = useMemo(() => {
    return [...(selected?.entries ?? [])].sort((a, b) =>
      a.field_key.localeCompare(b.field_key),
    );
  }, [selected]);

  const selectedReadOnly = labReadOnly || (selected?.is_system ?? false);

  return (
    <div className="p-6">
      <Link
        to="/laboratoire"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        <Trans>Laboratoire</Trans>
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Gauge className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            <Trans>Évaluateurs / Métriques</Trans>
          </h1>
          <p className="text-slate-600 mt-1">
            <Trans>
              Comment les expériences sont scorées : chaque champ est comparé à la
              réponse validée avec un évaluateur (exact-match, F1 sur une liste,
              tolérance numérique…) et compte selon son poids dans le score global.
            </Trans>
          </p>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <BookOpen className="h-4 w-4 text-primary" />
              <Trans>Contrat par défaut</Trans>
              {contract?.specialite && (
                <Badge
                  variant="outline"
                  className="text-xs bg-blue-50 text-blue-900 border-blue-200"
                >
                  {contract.specialite}
                </Badge>
              )}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              <Trans>
                Une expérience sans profil est scorée avec l'évaluateur et le poids
                que l'ontologie de la spécialité déclare pour chaque champ, lus au
                moment du calcul. Ils se modifient dans l'ontologie.
              </Trans>
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/laboratoire/ontologie">
                <Trans>Ontologie</Trans>
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setContractOpen(true)}
              disabled={!contract || contract.entries.length === 0}
            >
              <ListTree className="h-4 w-4 mr-1" />
              <Trans>Voir les champs</Trans>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {contract === null ? (
            <p className="text-sm text-slate-400">
              <Trans>Chargement...</Trans>
            </p>
          ) : !contract.specialite ? (
            <p className="text-sm text-slate-400">
              <Trans>
                Choisissez une spécialité pour voir le contrat de ses expériences.
              </Trans>
            </p>
          ) : contract.entries.length === 0 ? (
            <p className="text-sm text-slate-400">
              <Trans>Aucun champ d'ontologie pour cette spécialité.</Trans>
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              <Trans>
                {contract.scored_count} champs scorés sur {contract.entries.length}
              </Trans>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">
              <Trans>Profils personnalisés ({profiles.length})</Trans>
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              <Trans>
                Un profil remplace le contrat par défaut pour les expériences qui le
                choisissent : seuls ses champs sont scorés, avec ses évaluateurs et
                ses poids.
              </Trans>
            </p>
          </div>
          {!labReadOnly && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              <Trans>Nouveau</Trans>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-6">
              <Trans>Chargement...</Trans>
            </p>
          ) : profiles.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              <Trans>
                Aucun profil : les expériences sont scorées avec le contrat par défaut.
              </Trans>
            </p>
          ) : (
            <div className="space-y-3">
              {profiles.map((p) => {
                const cardReadOnly = labReadOnly || p.is_system;
                return (
                  <div
                    key={p.id}
                    className="flex items-start justify-between gap-3 p-4 rounded-lg border border-slate-200 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 truncate">
                          {p.name}
                        </span>
                        {p.is_system && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            <Trans>Système</Trans>
                          </Badge>
                        )}
                        {p.specialite && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-blue-50 text-blue-900 border-blue-200"
                          >
                            {p.specialite}
                          </Badge>
                        )}
                      </div>
                      {p.description && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                          {p.description}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        <Trans>{p.entry_count ?? 0} champs scorés</Trans>
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadProfileDetail(p.id)}
                      >
                        <ListTree className="h-4 w-4 mr-1" />
                        <Trans>Gérer les champs</Trans>
                      </Button>
                      {!cardReadOnly && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => setEditProfile(p)}
                            title={t`Modifier`}
                          >
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteProfile(p.id)}
                            title={t`Supprimer`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* The default contract's fields, read-only: they are edited in the ontology. */}
      <Dialog open={contractOpen} onOpenChange={setContractOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <DialogTitle>
                <Trans>Contrat par défaut</Trans>
                {contract?.specialite ? ` — ${contract.specialite}` : ""}
              </DialogTitle>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              <Trans>
                Lu dans l'ontologie, en lecture seule. Un champ sans évaluateur est lu
                mais jamais scoré.
              </Trans>
            </p>
          </DialogHeader>
          {contract && (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 mt-2">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50">
                <div className="col-span-7">
                  <Trans>Champ</Trans>
                </div>
                <div className="col-span-3">
                  <Trans>Évaluateur</Trans>
                </div>
                <div className="col-span-2 text-right">
                  <Trans>Poids</Trans>
                </div>
              </div>
              {contract.entries.map((e) => (
                <div
                  key={e.field_key}
                  className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm hover:bg-slate-50"
                >
                  <div className="col-span-7 min-w-0">
                    <div className="text-slate-900 truncate">{e.name}</div>
                    <div className="font-mono text-xs text-slate-400 truncate">
                      {e.field_key}
                    </div>
                  </div>
                  <div className="col-span-3 text-slate-600">
                    {e.evaluator_type ?? (
                      <span className="text-slate-400">
                        <Trans>non scoré</Trans>
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 text-right text-slate-600">
                    {e.weight ?? "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fields modal — replaces the legacy right-hand column. */}
      <Dialog
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Gauge className="h-5 w-5 text-primary" />
              <DialogTitle>
                {selected?.name ?? <Trans>Détails du profil</Trans>}
              </DialogTitle>
            </div>
            {selected?.description && (
              <p className="text-sm text-slate-500 mt-1">
                {selected.description}
              </p>
            )}
            {selected?.is_system && (
              <p className="text-xs text-slate-400 mt-1">
                <Trans>
                  Profil système en lecture seule. Clonez-le pour le personnaliser.
                </Trans>
              </p>
            )}
          </DialogHeader>
          {selected && (
            <div className="space-y-3 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">
                  <Trans>{sortedEntries.length} entrées</Trans>
                </span>
                {!selectedReadOnly && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setEntryEditor({ profileId: selected.id, entry: null })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    <Trans>Ajouter un champ</Trans>
                  </Button>
                )}
              </div>

              {sortedEntries.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">
                  <Trans>Aucune entrée — ajoutez-en pour scorer ce champ.</Trans>
                </p>
              ) : (
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50">
                    <div className="col-span-6">
                      <Trans>Champ</Trans>
                    </div>
                    <div className="col-span-3">
                      <Trans>Évaluateur</Trans>
                    </div>
                    <div className="col-span-2 text-right">
                      <Trans>Poids</Trans>
                    </div>
                    <div className="col-span-1" />
                  </div>
                  {sortedEntries.map((e) => (
                    <div
                      key={e.id}
                      className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm hover:bg-slate-50"
                    >
                      <div className="col-span-6 font-mono text-slate-700 truncate">
                        {e.field_key}
                      </div>
                      <div className="col-span-3 text-slate-600">
                        {e.evaluator_type}
                      </div>
                      <div className="col-span-2 text-right text-slate-600">
                        {e.weight}
                      </div>
                      <div className="col-span-1 flex justify-end gap-1">
                        {!selectedReadOnly && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() =>
                                setEntryEditor({
                                  profileId: selected.id,
                                  entry: e,
                                })
                              }
                            >
                              <Pencil className="h-3.5 w-3.5 text-slate-400" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500"
                              onClick={() =>
                                handleDeleteEntry(selected.id, e.id)
                              }
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ProfileEditorDialog
        open={createOpen}
        // Un profil créé pendant qu'on lit une spécialité lui appartient : le champ est prérempli
        // plutôt que retapé à la main. Il reste effaçable — un profil sans spécialité est partagé.
        defaultSpecialite={previewSpecialite}
        onClose={() => setCreateOpen(false)}
        onSave={async (data) => {
          await handleCreate(data);
          setCreateOpen(false);
        }}
      />

      <ProfileEditorDialog
        open={editProfile !== null}
        initial={editProfile ?? undefined}
        onClose={() => setEditProfile(null)}
        onSave={async (data) => {
          if (editProfile) {
            await handleUpdateProfile(editProfile.id, data);
            setEditProfile(null);
          }
        }}
      />

      <EntryEditorDialog
        open={entryEditor !== null}
        initial={entryEditor?.entry ?? undefined}
        onClose={() => setEntryEditor(null)}
        onSave={async (data) => {
          if (!entryEditor) return;
          if (entryEditor.entry) {
            await handleUpdateEntry(entryEditor.profileId, entryEditor.entry.id, {
              evaluator_type: data.evaluator_type,
              weight: data.weight,
            });
          } else {
            await handleAddEntry(entryEditor.profileId, data);
          }
          setEntryEditor(null);
        }}
      />
    </div>
  );
}

// ─── Dialogs ──────────────────────────────────────────────────────────────

function ProfileEditorDialog({
  open,
  initial,
  defaultSpecialite,
  onClose,
  onSave,
}: {
  open: boolean;
  initial?: MetricsProfile;
  defaultSpecialite?: string | null;
  onClose: () => void;
  onSave: (data: {
    name: string;
    description: string | null;
    specialite: string | null;
  }) => Promise<void>;
}) {
  const { t } = useLingui();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [specialite, setSpecialite] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setDescription(initial?.description ?? "");
      setSpecialite(initial?.specialite ?? defaultSpecialite ?? "");
    }
  }, [open, initial, defaultSpecialite]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        specialite: specialite.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {initial ? (
              <Trans>Modifier le profil</Trans>
            ) : (
              <Trans>Nouveau profil de métriques</Trans>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">
              <Trans>Nom</Trans>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t`Ex. : Profil strict rachis`}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">
              <Trans>Description (optionnelle)</Trans>
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t`Ex. : Évaluation stricte pour validation médicale`}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">
              <Trans>Spécialité (optionnelle)</Trans>
            </label>
            <Input
              value={specialite}
              onChange={(e) => setSpecialite(e.target.value)}
              placeholder={t`Ex. : rachis`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <Trans>Annuler</Trans>
          </Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? <Trans>Enregistrement...</Trans> : <Trans>Enregistrer</Trans>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntryEditorDialog({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial?: MetricsProfileEntry;
  onClose: () => void;
  onSave: (data: {
    field_key: string;
    evaluator_type: string;
    weight: number;
  }) => Promise<void>;
}) {
  const [fieldKey, setFieldKey] = useState("");
  const [evaluatorType, setEvaluatorType] = useState("string");
  const [weight, setWeight] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFieldKey(initial?.field_key ?? "");
      setEvaluatorType(initial?.evaluator_type ?? "string");
      setWeight(initial?.weight ?? 1);
    }
  }, [open, initial]);

  const isEdit = initial !== undefined;

  const handleSave = async () => {
    if (!fieldKey.trim() || !evaluatorType) return;
    setSaving(true);
    try {
      await onSave({
        field_key: fieldKey.trim(),
        evaluator_type: evaluatorType,
        weight: Math.max(1, weight),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? <Trans>Modifier l'entrée</Trans> : <Trans>Ajouter un champ</Trans>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">
              <Trans>Clé du champ (field_key)</Trans>
            </label>
            <Input
              value={fieldKey}
              onChange={(e) => setFieldKey(e.target.value)}
              placeholder="poids, tabac_actif, diagnostics_principaux..."
              disabled={isEdit}
              className="font-mono"
            />
            {isEdit && (
              <p className="text-xs text-slate-400">
                <Trans>La clé n'est pas modifiable. Supprimez et recréez l'entrée pour la changer.</Trans>
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">
              <Trans>Évaluateur</Trans>
            </label>
            <Select value={evaluatorType} onValueChange={setEvaluatorType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVALUATOR_TYPES.map((et) => (
                  <SelectItem key={et} value={et}>
                    {et}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">
              <Trans>Poids dans le score</Trans>
            </label>
            <Input
              type="number"
              min={1}
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value) || 1)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <Trans>Annuler</Trans>
          </Button>
          <Button onClick={handleSave} disabled={saving || !fieldKey.trim()}>
            {saving ? <Trans>Enregistrement...</Trans> : <Trans>Enregistrer</Trans>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
